// ====================================================================
// CHOCKY'S CLOSET — the outfit store in the main village
// ====================================================================
// A shop building in the south lane (between POP SAFARI and the south fence)
// that sells the outfits in game/outfits.js for coins. Walk up to the door and
// a proximity card appears (same hysteresis pattern as the game huts); ENTER
// or tapping the card opens a DOM shop panel (design plan §7: UI is DOM, not
// in-canvas). Item thumbnails are CSS swatches and emoji — never live WebGL
// views (design plan §6). Walking away closes everything, which is the
// kid-proof escape hatch; ✕ and ESC work too.
//
// The world geometry is built through ctx builders passed from main.js (the
// zone pattern) and the wallet stays main.js-owned: this module only calls
// ctx.coins.count()/spend(). Ownership + equipped state persist via
// game/save.js.

import { loadSave, patchSave } from './save.js';
import { CATALOG, equip, getEquipped } from './outfits.js';

const STORE_X = -3.4, STORE_Z = -7.4;
const DOOR_X = STORE_X;
let DOOR_Z = STORE_Z + 2.2 / 2 + 0.5;  // +z face, like the huts; re-derived from the kit model's depth in initStore
const TRIGGER_DIST = 1.4, RELEASE_DIST = 1.9;              // hut-card hysteresis
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}[ch]));
const safeCssColor = (value) => /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(String(value || '').trim())
  ? String(value).trim()
  : '#f9e8d4';

export function initStore(ctx) {
  // ---- the building --------------------------------------------------------
  // Handmade closet model from the kit (mirror + toy box and all); the pink
  // procedural house is the fallback when the kit didn't load.
  const kitShop = ctx.buildKitStructure && ctx.buildKitStructure('chocky_closet', STORE_X, STORE_Z, { name: "Chocky's Closet" });
  if (!kitShop) ctx.buildHouse(STORE_X, STORE_Z, 2.4, 2.2, 1.6, 0xf2d8e4, 0x9c4a78, false);
  else DOOR_Z = STORE_Z + kitShop.userData.kitSize.d / 2 + 0.5;
  ctx.addHutSign(STORE_X, STORE_Z, "CHOCKY'S CLOSET");
  ctx.addVillageLandmark(STORE_X, STORE_Z, 2.6);           // safe checkpoint too
  ctx.buildPath([[0, -3.5], [-1.8, -5.4], [DOOR_X, -5.9]]); // spur off the well plaza

  // ---- DOM: proximity card + shop panel ------------------------------------
  const card = document.createElement('div');
  card.id = 'storeCard';
  card.style.cssText =
    'display:none; position:fixed; bottom:80px; left:50%; transform:translateX(-50%);' +
    'background:rgba(255,255,255,0.97); color:#1b1f2a; padding:18px 28px;' +
    'border-radius:16px; font:600 18px/1.3 inherit; box-shadow:0 12px 40px rgba(0,0,0,0.18);' +
    'border:2px solid #9c4a78; text-align:center; pointer-events:auto; cursor:pointer; z-index:30;';
  card.innerHTML =
    '<div style="font-size:13px; opacity:0.65;">Outfit Store</div>' +
    "<div>CHOCKY'S CLOSET</div>" +
    '<div style="font-size:13px; opacity:0.65;">Press <kbd style="background:#9c4a78; color:white; padding:2px 8px; border-radius:4px; font-family:ui-monospace,monospace;">ENTER</kbd> to shop &nbsp;·&nbsp; or tap this card</div>';
  document.body.appendChild(card);

  const panel = document.createElement('div');
  panel.id = 'storePanel';
  panel.style.cssText =
    'display:none; position:fixed; top:50%; left:50%; transform:translate(-50%,-50%);' +
    'background:rgba(255,255,255,0.97); color:#1b1f2a; padding:20px 22px; border-radius:20px;' +
    'font:600 16px/1.3 inherit; box-shadow:0 16px 60px rgba(0,0,0,0.3); border:3px solid #9c4a78;' +
    'z-index:40; width:min(92vw, 460px); max-height:80vh; overflow-y:auto; pointer-events:auto;';
  document.body.appendChild(panel);

  let near = false;
  let open = false;

  function ownedSet() { return new Set(loadSave().owned); }

  function renderPanel() {
    const owned = ownedSet();
    const equipped = getEquipped();
    const itemRow = (item) => {
      const isOwned = owned.has(item.id);
      const isEquipped = equipped[item.slot] === item.id;
      const safeId = escapeHtml(item.id);
      const safeName = escapeHtml(item.name);
      const safeEmoji = escapeHtml(item.emoji);
      const safeQuest = escapeHtml(item.quest);
      const thumb = item.swatch
        ? `<span style="display:inline-block; width:34px; height:34px; border-radius:50%; background:${safeCssColor(item.swatch)}; border:2px solid #1b1f2a22; vertical-align:middle;"></span>`
        : `<span style="font-size:28px; vertical-align:middle;">${safeEmoji}</span>`;
      let btn;
      if (!isOwned && item.quest) {
        // Quest rewards aren't for sale — the store just points the way.
        btn = `<span style="font-size:12px; opacity:0.7; max-width:150px; text-align:right;">🔍 ${safeQuest}</span>`;
      } else if (!isOwned) {
        const afford = ctx.coins.count() >= item.price;
        btn = `<button data-act="buy" data-id="${safeId}" ${afford ? '' : 'disabled'}
          style="font:inherit; padding:8px 14px; border-radius:10px; border:none; cursor:${afford ? 'pointer' : 'default'};
          background:${afford ? '#ffb703' : '#e5e0d8'}; color:${afford ? '#1b1f2a' : '#999'};">Buy ${item.price} 🪙</button>`;
      } else if (isEquipped) {
        btn = `<button data-act="unequip" data-id="${safeId}"
          style="font:inherit; padding:8px 14px; border-radius:10px; border:none; cursor:pointer;
          background:#7cb56b; color:white;">Wearing ✓</button>`;
      } else {
        btn = `<button data-act="equip" data-id="${safeId}"
          style="font:inherit; padding:8px 14px; border-radius:10px; border:none; cursor:pointer;
          background:#9c4a78; color:white;">Wear</button>`;
      }
      return `<div style="display:flex; align-items:center; gap:12px; padding:8px 4px; border-bottom:1px solid #1b1f2a14;">
        ${thumb}
        <div style="flex:1; text-align:left;">
          <div>${safeName}</div>
          <div style="font-size:12px; opacity:0.6;">${item.slot === 'body' ? 'Costume' : 'Hat'}</div>
        </div>
        ${btn}
      </div>`;
    };
    const section = (slot, label) =>
      `<div style="font-size:13px; letter-spacing:0.08em; opacity:0.55; margin:12px 0 2px; text-align:left;">${label}</div>` +
      CATALOG.filter(it => it.slot === slot).map(itemRow).join('');
    panel.innerHTML =
      `<div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:8px;">
        <div style="font-size:20px;">CHOCKY'S CLOSET</div>
        <button id="storeClose" style="font:inherit; font-size:18px; border:none; background:none; cursor:pointer; padding:4px 8px;">✕</button>
      </div>
      <div style="font-size:14px; opacity:0.75; margin-bottom:2px;">Your coins: 🪙 ${ctx.coins.count()}</div>
      ${section('body', 'COSTUMES')}
      ${section('head', 'HATS')}
      <div style="font-size:12px; opacity:0.5; margin-top:10px;">Walk away to close · collect coins around the village!</div>`;
  }

  function openPanel() {
    if (open) return;
    open = true;
    renderPanel();
    panel.style.display = 'block';
    card.style.display = 'none';
  }
  function closePanel() {
    if (!open) return;
    open = false;
    panel.style.display = 'none';
  }

  panel.addEventListener('click', (e) => {
    const btn = e.target.closest('button');
    if (!btn) return;
    if (btn.id === 'storeClose') { closePanel(); return; }
    const { act, id } = btn.dataset;
    const item = CATALOG.find(it => it.id === id);
    if (!item) return;
    if (act === 'buy') {
      if (ctx.coins.count() < item.price) return;
      ctx.coins.spend(item.price);
      const owned = ownedSet();
      owned.add(item.id);
      patchSave({ owned: [...owned] });
      equip(item.slot, item.id);          // new gear goes straight on
    } else if (act === 'equip') {
      equip(item.slot, item.id);
    } else if (act === 'unequip') {
      equip(item.slot, null);             // body falls back to Classic Cream
    }
    renderPanel();
  });

  card.addEventListener('click', () => { if (near && ctx.isPlaying()) openPanel(); });

  addEventListener('keydown', (e) => {
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (!ctx.isPlaying()) return;
    if (e.key === 'Enter' && near && !open) { e.preventDefault(); openPanel(); }
    else if (e.key === 'Escape' && open) closePanel();
  });

  function update() {
    const p = ctx.getPlayerPos();
    const d = Math.hypot(p.x - DOOR_X, p.z - DOOR_Z);
    if (d < TRIGGER_DIST) near = true;
    else if (d > RELEASE_DIST) near = false;
    card.style.display = near && !open ? 'block' : 'none';
    if (open && !near) closePanel();      // walking away is the escape hatch
  }

  function close() {
    near = false;
    closePanel();
    card.style.display = 'none';
  }

  return { update, close };
}
